import { getToken } from '@/api/upload'
import { apiGetCountryList } from '@/api/user'
import { apiGetCountryList as apiGetContinentCountryList } from '@/api/home'
import { apiGetLuckyPopup } from '@/api/public'
import type { countryInterface } from '@/types/apiType/userType'
import type { continentCountryInterface } from '@/types/apiType/homeType'

export const usePublicStore = defineStore({
  id: 'public',
  state: () => ({
    isShowView: false, // 是否显示视图，用做首页国家
    freeCardPopupConfig: {
      // 是否显示获得免费券弹窗
      isShow: false,
      isLazyShow: false
    },
    rechargePopupConfig: {
      //  是否显示充值弹窗
      isShow: false,
      isLazyShow: false
    },
    qnToken: '', //七牛云上传token
    countryLanguage: '' as string, // 国家语言,用来做判断国家列表获取使用
    countryList: [] as countryInterface[], //国家列表
    continentCountryList: [] as continentCountryInterface[], // 国家大洲列表
    rechargeTipsPopupConfig: {
      isShow: false, // 是否显示充值提示
      isLazyShow: false,
      tipsText: useNuxtApp().$i18n.t('call.insufficientBalanceTips') as string, // 是否显示充值提示的默认提示语
      toType: 'page' as 'page' | 'popup' // 点击跳转类型 page:充值页面, popup:充值弹窗
    },
    /** 引流至App-提示 */
    downloadAppTipsPopupConfig: {
      isShow: false, // 引流至App提示
      isLazyShow: false,
      tipsText: useNuxtApp().$i18n.t('downloadAppTips.GetTips') as string, // 引流至App提示
      trackingLogType: 'msg' as 'msg' | 'call' | 'live' // 埋点类型: 消息页，通话后，直播列表页
    },
    /** 引流至App-幸运用户 */
    downloadAppLuckyPopupConfig: {
      isShow: false,
      isLazyShow: false,
      /** 倒计时毫秒数 */
      countdownMillis: 0 as number
    },
    /** 苹果设备的UDID，用于开播检查使用，本地默认使用，UDID 00008030-001E29493EF2802E*/
    iosUDID: '',
    /** 页面是否显示 */
    isPageVisible: true as boolean,
    /** 当前路径 */
    currentPath: '' as string
  }),
  getters: {
    isFirstPage: (state) => {
      return (
        state.currentPath === '/' ||
        state.currentPath == '/home' ||
        state.currentPath == '/chat' ||
        state.currentPath == '/aboutme'
      )
    }
  },
  actions: {
    /**
     * 同步当前路径字段
     */
    setCurrentPath(value: string) {
      this.currentPath = value
    },
    /**
     * 设置页面是否显示
     **/
    setIsPageVisible(value: boolean) {
      this.isPageVisible = value
    },
    /**
     * 设置UDID
     */
    setIOSUDID(value: string) {
      this.iosUDID = value
    },
    /**
     * 引流至App
     * value: 显示或开启
     * content: string:字符串类型: 直接赋值内容
     *          1:Get the full experience on the app
     *          2:Free text messaging on the app
     *          3：Download the app to send messages for free and unlock more rewards
     * trackingLogType: 'msg' | 'call' | 'live'  埋点类型: 消息页，通话后，直播列表页
     */
    setDownloadAppTipsConfig(
      value: boolean,
      content: 1 | 2 | 3 | string,
      trackingLogType: 'msg' | 'call' | 'live'
    ) {
      const i18n = useNuxtApp().$i18n
      this.downloadAppTipsPopupConfig.isShow = value
      // 为true再走逻辑
      if (value) {
        this.downloadAppTipsPopupConfig.isLazyShow = true
        this.downloadAppTipsPopupConfig.trackingLogType = trackingLogType
        // 引流至App弹窗显示次数埋点，按细分
        switch (trackingLogType) {
          case 'msg':
            eventTracking.eventLog(
              eventTracking.eventId.msg_go_to_download_popup
            )
            break
          case 'call':
            eventTracking.eventLog(
              eventTracking.eventId.call_go_to_download_popup
            )
            break
          case 'live':
            eventTracking.eventLog(
              eventTracking.eventId.live_go_to_download_popup
            )
            break
        }
        // 设置文案
        switch (content) {
          case 1:
            this.downloadAppTipsPopupConfig.tipsText = i18n.t(
              'downloadAppTips.GetTips'
            )
            break
          case 2:
            this.downloadAppTipsPopupConfig.tipsText = i18n.t(
              'downloadAppTips.FreeTips'
            )
            break
          case 3:
            this.downloadAppTipsPopupConfig.tipsText = i18n.t(
              'downloadAppTips.DownloadApp2'
            )
            break
          default:
            this.downloadAppTipsPopupConfig.tipsText = content || ''
            break
        }
      }
    },
    /**
     * 引流至App-幸运用户
     * value: 显示或开启
     */
    setDownloadAppLuckyPopupConfig(value: boolean) {
      this.downloadAppLuckyPopupConfig.isShow = value
      if (value) {
        this.downloadAppTipsPopupConfig.isLazyShow = true
      }
    },
    /**
     * 幸运用户弹窗倒计时获取
     * @param isShow: 显示或开启弹窗
     */
    getLuckyPopup(isShow: boolean) {
      // 苹果设备不做此功能
      if (common.checkVersion().ios) return
      apiGetLuckyPopup().then((res) => {
        if (res.code === 200 && res.data && res.data.countdownMillis) {
          this.downloadAppLuckyPopupConfig.countdownMillis =
            res.data.countdownMillis
          if (this.downloadAppLuckyPopupConfig.countdownMillis > 0) {
            this.setDownloadAppLuckyPopupConfig(Boolean(isShow))
          }
        }
      })
    },
    // 设置是否显示充值弹窗
    setShowRechargePopup(isShow: boolean) {
      const userStore = useUserStore()
      this.rechargePopupConfig.isLazyShow = true
      this.rechargePopupConfig.isShow = isShow
      // 充值弹窗埋点
      if (this.rechargePopupConfig.isShow) {
        eventTracking.eventLog(eventTracking.eventId.open_charge_popup)
        // 新用户充值弹窗埋点
        if (userStore.isNewUser) {
          eventTracking.eventLog(
            eventTracking.eventId.open_charge_popup_new_user
          )
        }
      }
    },
    // 设置免费券弹窗显示
    setShowFreeCardPopup(isShow: boolean) {
      this.freeCardPopupConfig.isLazyShow = true
      this.freeCardPopupConfig.isShow = isShow
    },

    /**
     * 是否显示充值提示
     * value: 显示或开启
     * content: string:字符串类型: 直接赋值内容
     *          1:抱歉，你的余额不足，请充值，
     *          2:仅充值一次您就可以开启美好对话！
     *          3:会话上传图片，等级限制提示
     * toType: 'page' as 'page' | 'popup' 点击跳转类型 page:充值页面, popup:充值弹窗，不传则为page
     */
    setRechargeTipsPopup(
      value: boolean,
      content: 1 | 2 | 3 | string,
      toType?: 'page' | 'popup'
    ) {
      const i18n = useNuxtApp().$i18n
      this.rechargeTipsPopupConfig.isShow = value
      this.rechargeTipsPopupConfig.isLazyShow = true
      if (this.rechargeTipsPopupConfig.isShow) {
        this.rechargeTipsPopupConfig.toType = toType || 'page'
        const chatStore = useChatStore()
        switch (content) {
          case 1:
            this.rechargeTipsPopupConfig.tipsText = i18n.t(
              'call.insufficientBalanceTips'
            )
            break
          case 2:
            this.rechargeTipsPopupConfig.tipsText = i18n.t('chat.paidChatMsg')
            break
          case 3:
            this.rechargeTipsPopupConfig.tipsText = i18n.t('chat.sendPicTips', [
              chatStore.freeMsgCount.limitLv
            ])
            break
          default:
            this.rechargeTipsPopupConfig.tipsText = content || ''
            break
        }
      }
    },

    /**
     * 重置登录
     * @param {string} msg 踢出来源，目前做console.log提示
     * @param {string | number} type 踢出类型： init,api,im,socket,socket-kickoff,click
     * @param {boolean} direction 可选，默认true,是否重定向跳转首页
     */
    resetLogin({
      msg,
      type,
      direction = true
    }: {
      msg: string
      type: string | number
      direction?: boolean
    }) {
      try {
        const runtimeConfig = useRuntimeConfig()
        const i18n = useNuxtApp().$i18n
        const router = useRouter()
        console.log('重置登录函数被触发=>', { msg, type, direction })

        const socketStore = useSocketStore()
        const userStore = useUserStore()
        const chatStore = useChatStore()
        const agoraStore = useAgoraStore()
        const trtcStore = useTrtcStore()
        const callStore = useCallStore()
        const roomStore = useRoomStore()
        const rechargeStore = useRechargeStore()
        const giftStore = useGiftStore()
        const animationStore = useAnimationStore()

        // 重置数据
        chatStore.disconnect()
        socketStore.destroy()
        userStore.resetData()
        chatStore.resetData()
        agoraStore.resetData()
        trtcStore.resetData()
        callStore.resetData()
        roomStore.resetData()
        rechargeStore.resetData()
        giftStore.resetData()
        animationStore.resetData()

        userStore.changeManOnline(2) // 退登前调用,会话打招呼使用

        // 关闭房间和1v1离开页面确认框
        callStore.setIsShowCallRouteLeavDialog(false)
        roomStore.setIsShowLiveRouteLeavDialog(false)

        userStore.userInfo = defaultData.defaultUserInfo()
        localStorage.removeItem('ticket')
        localStorage.removeItem('loginInfo')
        this.reset()

        // gtag 重置设置用户ID
        eventTracking.setUid('')

        // 类型-提示
        switch (type) {
          case 'im': // im被踢
            showToast(i18n.t('public.kicked'))
            break
          case 'socket-kickoff':
            showToast(i18n.t('public.kicked'))
            break
          default:
            break
        }

        // FB像素ID移除做特殊处理，init时不移除，暂时不做移除处理
        if (type !== 'init') {
          // localStorage.removeItem('utmFacebookPixelId')
        }

        // 是否重定向首页，不在APP内，当前域名要和 cdnUrl 一样才可以重定向
        if (
          direction &&
          !roomStore.isApp &&
          (location.origin === runtimeConfig.public.cdnUrl ||
            runtimeConfig.public.appMode === 'development')
        ) {
          router.push('/')
        }
      } catch (error) {
        console.log('退出登录报错==>', error)
      }
    },
    /**
     * 重置函数
     */
    reset() {
      this.qnToken = ''
      this.countryList = []
      this.continentCountryList = []
      this.downloadAppLuckyPopupConfig.countdownMillis = 0
    },
    // 设置显示视图
    setIsShowView() {
      this.isShowView = true
    },
    // 七牛云上传token
    setQnToken() {
      getToken().then((res) => {
        const { code, data } = res
        if (code === 200) {
          this.qnToken = data
        }
      })
    },
    // 国家列表
    async setCountryList() {
      if (this.countryList.length) {
        return
      }
      await apiGetCountryList().then(
        (res: { code: number; data: countryInterface[] }) => {
          const { code, data } = res
          if (code === 200) {
            this.countryList = data
          }
        }
      )
    },
    // 获取大洲列表
    async getContinentCountryList() {
      const languageStore = useLanguageStore()
      // 做语言判断，语言和之前的不一致，就更新数据
      if (
        this.continentCountryList.length &&
        languageStore.languageInfo.value === this.countryLanguage
      )
        return
      const res = await apiGetContinentCountryList()
      this.countryLanguage = languageStore.languageInfo.value
      const { code, data } = res
      if (code === 200) {
        data.forEach((val) => {
          val.countryList = val.countryList.filter((v) => {
            return v.countryId
          })
        })
        this.continentCountryList = data
      }
    }
  },
  persist: {
    key: 'publicStore',
    paths: [
      'qnToken',
      'countryList',
      'continentCountryList',
      'countryLanguage',
      'iosUDID'
    ]
  }
})
